<template>
  <PageLayout>
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <breadcrumb :list="breadcrumbList" />
        <GroupsTreeSelect @selectItem="selectGroupItem" />
      </div>
    </template>
    <template slot="main">
      <!-- Loading -->
      <div v-if="isLoading" class="loading">
        <loading />
        <p>{{ benchmarkResources.loading }}</p>
      </div>

      <!-- Empty -->
      <div v-if="!isLoading && !benchmark" class="empty">
        <img alt="empty" src="benchmark/empty-state.gif" />
      </div>

      <!-- Ok -->
      <div v-if="!isLoading && benchmark">
        <b-card class="mb-4">
          <header slot="header">
            <div class="title">
              {{ benchmarkResources.globalIndicators }}
            </div>
            <b-button variant="link" class="info" v-b-modal.modal-details>{{
              benchmarkResources.modal.title
            }}</b-button>
          </header>
          <div class="globals">
            <BenchScoreCard
              :key="indicator.id"
              v-for="indicator in benchmark.globalIndicadors"
              :title="benchmarkResources.globalIndicatorsList[indicator.id]"
              :score="indicator.score"
              :average="indicator.average"
              :percentRank="indicator.percentRank"
              :base="indicator.id === 'engagement' ? '0|10' : '-100|100'"
            />
          </div>
        </b-card>

        <b-card class="mb-4">
          <header slot="header" class="title">
            {{ benchmarkResources.engagementIndicators }}
          </header>
          <div class="globals">
            <BenchScoreCard
              :key="indicator.id"
              v-for="indicator in benchmark.engagementIndicators"
              :title="benchmarkResources.engagementIndicatorsList[indicator.id]"
              :score="indicator.score"
              :average="indicator.average"
              :percentRank="indicator.percentRank"
              base="0|10"
            />
          </div>
        </b-card>
      </div>

      <div class="axel-tutorial" v-if="!isLoading">
        <img alt="axel" src="/home/analytics/super_axel.png" />
        <div>
          <b>{{ benchmarkResources.axelTip.title }}</b>
          <p class="warning" v-if="!benchmark">
            {{ benchmarkResources.axelTip.emptyA }}
          </p>
          <p v-if="!benchmark">
            {{ benchmarkResources.axelTip.emptyB }}
            <b>{{ benchmarkResources.axelTip.engagementReport }}</b>
          </p>
          <p>
            {{ benchmarkResources.axelTip.tipA }}<br /><br />
            {{ benchmarkResources.axelTip.tipB }}
          </p>
        </div>
      </div>

      <!-- Modal Plans -->
      <modal-plans
        ref="component-modal-plans"
        title="Benchmark"
        :hideHeaderClose="true"
        :visible="true"
        v-if="!$can('use', 'actionPlan')"
        @hide="handleHide"
      />
      <!-- END - Modal Plans -->

      <b-modal id="modal-details" :title="benchmarkResources.modal.title">
        <div class="d-block text-center">
          <img alt="" :src="benchmarkResources.modal.img" contain />
        </div>
        <template #modal-footer class="left">
          <p class="left text-left">
            {{ benchmarkResources.modal.footerA }}
            {{ benchmarkResources.modal.footerB }}
          </p>
        </template>
      </b-modal>

      <b-modal
        id="modal-unauthorized"
        :title="benchmarkResources.unauthorizedModal.title"
        cancel-variant="outline-primary"
        ref="modal-unauthorized"
        header-bg-variant="danger"
        @hide="handleHide"
      >
        <p class="txt-20">{{ benchmarkResources.unauthorizedModal.message }}</p>
        <template v-slot:modal-footer>
          &nbsp;
        </template>
      </b-modal>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import { getUser } from '@/service/user'
import Loading from '@/components/Shared/Loading.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import BenchScoreCard from '@/components/Benchmark/BenchScoreCard.vue'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'

export default {
  components: {
    Loading,
    ModalPlans,
    Breadcrumb,
    PageLayout,
    BenchScoreCard,
    GroupsTreeSelect,
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      currentGroup: 'user/currentGroup',
      benchmark: 'benchmark/benchmark',
      isLoading: 'benchmark/isLoading',
      selectedGroup: 'user/selectedGroup',
    }),
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.report,
        },
        {
          label: this.resources.general.menu.benchmark,
        },
      ]
    },
    benchmarkResources() {
      return this.resources.page.reports.benchmark
    },
    groupId: function() {
      return this.$route.params.groupId
    },
    userRole() {
      return this.user.role
    },
    user() {
      return getUser()
    },
  },

  methods: {
    selectGroupItem(group) {
      if (!group.enable) {
        this.$refs['component-modal-plans'].show()
        return false
      }
      this.loadBenchmark(group._id)
      this.$router.push({
        name: 'benchmark',
        params: { groupId: group._id },
      })
      this.$store.commit('user/updateCurrentGroup', group)
      this.groupSelected = group
    },
    async loadBenchmark(groupId) {
      try {
        await this.$store.dispatch('benchmark/getBenchmarkByGroup', groupId)
      } catch (error) {
        const { status } = error.response
        if (status === 401) {
          this.$refs['modal-unauthorized'].show()
        }
      }
    },
    handleHide() {
      this.$router.push({ name: 'dashboard' })
    },
  },
  created() {
    const selectedGroup = this.selectedGroup
    if (selectedGroup && selectedGroup !== this.groupId) {
      this.loadBenchmark(selectedGroup)
      return this.$router.push({
        name: 'benchmark',
        params: { groupId: selectedGroup },
      })
    }
    this.loadBenchmark(this.groupId)
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

#modal-details {
  img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 3px 8px 26px #0000000d;
  }
}

.d-flex {
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.loading {
  margin: 0 auto;
  text-align: center;
}

.empty {
  margin-bottom: 10px;
  img {
    display: block;
    max-width: 80%;
    max-height: 500px;
    margin: auto;
  }
}

.axel-tutorial {
  display: flex;
  margin-top: 30px;
  margin-bottom: 10px;
  img {
    max-height: 100px;
    min-width: 100px;
  }
  div {
    background-color: map-get($theme-colors, 'white');
    margin: 0px 10px 10px 10px;
    padding: 20px;
    box-shadow: 3px 8px 26px #0000000d;
    border: 1px solid #7070701f;
    border-radius: 20px;
  }
  .warning {
    font-size: 1.2rem;
    font-weight: 600;
    color: orangered;
  }
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .title {
    font-weight: 600;
  }
  .info {
    font-size: 1rem;
    color: #344ec6;
    text-transform: none !important;
    border: none;
    outline: none;
  }
}

.globals {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
@include media-breakpoint-down(sm) {
  .axel-tutorial {
    flex-direction: column;
    img {
      margin: auto;
      max-width: 100px;
    }
  }
}
</style>
